import { useFormik } from "formik";
import { Dropdown } from "primereact/dropdown";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdAddCircleOutline } from "react-icons/md";
import { useDispatch } from "react-redux";

import useAuth from "../../../hooks/useAuth";
import { useSnack } from "../../../hooks/useSnack";
import useUserType from "../../../hooks/useUserType";
import { setUser } from "../../../redux/slices/userSlice";
import classroomServices from "../../../services/classroomServices";
import schoolServices from "../../../services/schoolServices";
import teacherServices from "../../../services/teacherServices";
import { addClassroomModalValidationSchema } from "../../../utils/validationSchema";
import Button from "../../Button";
import Modal from "../../Modal";

const AddClassroomModal = forwardRef(({ onSubmit }, ref) => {
  const { isSchool } = useUserType();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTeacherID, setSelectedTeacherID] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [courses, setCourses] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showSnack } = useSnack();
  const dispatch = useDispatch();
  const user = useAuth();

  useImperativeHandle(ref, () => ({
    openModal(selectedTeacherID) {
      setSelectedTeacherID(selectedTeacherID);
      setIsOpen(true);
    },
    closeModal() {
      setIsOpen(false);
    },
  }));

  const formik = useFormik({
    initialValues: {
      classroomName: "",
      section: "",
      teacher: isSchool ? null : user?.teacher_id,
      grade: null,
      course: null,
    },
    validationSchema: addClassroomModalValidationSchema(t),
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      try {
        const payload = {
          name: values.classroomName,
          section: values.section,
          teacher_id: values.teacher,
          school_grade_id: values.grade,
          course_id: values.course,
        };

        const response = await classroomServices.addNewClassroom(payload);
        showSnack(response.message, "success");
        resetForm();
        onSubmit(values);
        setIsOpen(false);
        setTimeout(() => {
          setIsSubmitting(false);
        }, 500);

        if (!isSchool) {
          // Updates the teacher's view when a new classroom with level-based courses is added,
          // so the "Project Assessment" appears in the sidebar menu.
          const teacherResponse = await teacherServices.getTeacherData();
          const updatedUser = {
            ...user,
            has_children_courses: teacherResponse?.has_children_courses,
            has_other_courses: teacherResponse?.has_other_courses,
          };
          dispatch(setUser(updatedUser));
        }
      } catch (error) {
        setIsSubmitting(false);
        showSnack(error, "error");
      }
    },
  });

  const getTeachers = async () => {
    setLoadingTeachers(true);
    try {
      const response = await schoolServices.getTeachersBySchool(user?.school?.id);
      const formattedTeachers = response.data
        .filter((item) => !item.is_disabled)
        .map((teacher) => ({
          label: teacher.name,
          value: teacher.id,
        }));

      setTeachers(formattedTeachers);
      const selectedTeacher = formattedTeachers.find((teacher) => teacher.value == selectedTeacherID);
      if (selectedTeacher) {
        formik.setFieldValue("teacher", selectedTeacher.value);
      }
    } catch (error) {
      console.error("Error fetching teachers data:", error);
    } finally {
      setLoadingTeachers(false);
    }
  };

  const getGrades = async () => {
    setLoadingGrades(true);
    try {
      const response = await schoolServices.getGradesBySchool();
      const formattedGrades = response.map((grade) => ({
        label: grade.name,
        value: grade.id,
      }));
      setGrades(formattedGrades);
    } catch (error) {
      console.error("Error fetching teachers data:", error);
    } finally {
      setLoadingGrades(false);
    }
  };

  const getCourses = async () => {
    setLoadingCourses(true);
    try {
      const response = await schoolServices.getCoursesBySchool();
      const formattedCourses = response.courses.map((course) => ({
        label: course.course,
        value: course.id,
      }));
      setCourses(formattedCourses);
    } catch (error) {
      console.error("Error fetching teachers data:", error);
    } finally {
      setLoadingCourses(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (isSchool) {
        getTeachers();
      }
      getGrades();
      getCourses();
    }
  }, [isOpen]);

  const handleCloseModal = () => {
    formik.resetForm(); // Reset the form
    setIsOpen(false); // Close the modal
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      header={
        <p className="flex items-center gap-2 text-green-500">
          <MdAddCircleOutline size={22} />
          {t("add_new_classroom")}
        </p>
      }
      footer={
        <div className="flex items-center justify-end space-x-2">
          <Button color="white" variant="outlined" onClick={handleCloseModal} label={t("cancel")} />
          <Button
            className="w-36"
            color="green"
            disabled={isSubmitting}
            loading={isSubmitting}
            onClick={formik.handleSubmit}
            label={t("create")}
          />
        </div>
      }
    >
      <form>
        <div className="grid grid-cols-2 gap-x-3 gap-y-2">
          <div>
            <div className="mb-1 flex items-end space-x-1">
              <label className="block text-sm font-normal text-gray-800 dark:text-white">
                {t("classroom_name")}
              </label>
              {formik.touched.classroomName &&
                (formik.errors.classroomName === "Required" || formik.errors.classroomName === "مطلوب") && (
                  <span className="text-start text-xl leading-3 text-red-300">*</span>
                )}
            </div>
            <input
              type="text"
              name="classroomName"
              placeholder={t("classroom_name")}
              value={formik.values.classroomName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="block w-full rounded-md border bg-[#fcfdfd] px-3 py-2 text-sm placeholder:text-sm placeholder:font-light placeholder:text-mutedText focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-10 dark:border-dark-background dark:bg-dark-background dark:text-dark-mutedText"
            />
            {formik.touched.classroomName &&
              formik.errors.classroomName !== "Required" &&
              formik.errors.classroomName !== "مطلوب" && (
                <p className="mt-1 text-start text-xs text-red-300">{formik.errors.classroomName}</p>
              )}
          </div>

          <div>
            <div className="mb-1 flex items-end space-x-1">
              <label className="block text-sm font-normal text-gray-800 dark:text-white">
                {t("section")}
              </label>
              {formik.touched.section &&
                (formik.errors.section === "Required" || formik.errors.section === "مطلوب") && (
                  <span className="text-start text-xl leading-3 text-red-300">*</span>
                )}
            </div>
            <input
              type="text"
              name="section"
              placeholder={t("section")}
              value={formik.values.section}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="block w-full rounded-md border bg-[#fcfdfd] px-3 py-2 text-sm placeholder:text-sm placeholder:font-light placeholder:text-mutedText focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-10 dark:border-dark-background dark:bg-dark-background dark:text-dark-mutedText"
            />
            {formik.touched.section &&
              formik.errors.section !== "Required" &&
              formik.errors.section !== "مطلوب" && (
                <p className="mt-1 text-start text-xs text-red-300">{formik.errors.section}</p>
              )}
          </div>
          {/* Grade */}
          <div>
            <div className="mb-1 flex items-end space-x-1">
              <label className="block text-sm font-normal text-gray-800 dark:text-white">{t("grade")}</label>
              {formik.touched.grade &&
                (formik.errors.grade === "Required" || formik.errors.grade === "مطلوب") && (
                  <span className="text-start text-xl leading-3 text-red-300">*</span>
                )}
            </div>
            <Dropdown
              pt={{
                wrapper: "text-sm",
                header: "bg-blue-200 p-2 rounded-t-md",
                input:
                  "p-0 text-sm placeholder:text-sm placeholder:text-mutedText dark:text-dark-mutedText dark:text-white",
                root: "w-full rounded-md border bg-[#F9FAFB] px-3 py-2 focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-10 dark:border-dark-background dark:bg-dark-background",
                panel:
                  "bg-white dark:bg-gray-800 shadow-lg rounded-md border border-gray-200 dark:border-gray-700",
                item: "p-2 hover:bg-green-100 dark:hover:bg-green-700",
              }}
              value={formik.values.grade}
              onChange={(e) => formik.setFieldValue("grade", e.value)}
              options={grades}
              optionLabel="label"
              optionValue="value"
              placeholder={t("select_grade")}
              className="w-full"
              filter
              emptyFilterMessage={t("no_results_found")}
            />
            {formik.touched.grade &&
              formik.errors.grade !== "Required" &&
              formik.errors.grade !== "مطلوب" && (
                <p className="mt-1 text-start text-xs text-red-300">{formik.errors.grade}</p>
              )}
          </div>

          {/* Course */}
          <div>
            <div className="mb-1 flex items-end space-x-1">
              <label className="block text-sm font-normal text-gray-800 dark:text-white">{t("course")}</label>
              {formik.touched.course &&
                (formik.errors.course === "Required" || formik.errors.course === "مطلوب") && (
                  <span className="text-start text-xl leading-3 text-red-300">*</span>
                )}
            </div>
            <Dropdown
              pt={{
                wrapper: "text-sm",
                header: "bg-blue-200 p-2 rounded-t-md",
                input:
                  "p-0 text-sm placeholder:text-sm placeholder:text-mutedText dark:text-dark-mutedText dark:text-white",
                root: "w-full rounded-md border bg-[#F9FAFB] px-3 py-2 focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-10 dark:border-dark-background dark:bg-dark-background",
                panel:
                  "bg-white dark:bg-gray-800 shadow-lg rounded-md border border-gray-200 dark:border-gray-700",
                item: "p-2 hover:bg-green-100 dark:hover:bg-green-700",
              }}
              value={formik.values.course}
              onChange={(e) => formik.setFieldValue("course", e.value)}
              options={courses}
              optionLabel="label"
              optionValue="value"
              placeholder={t("select_course")}
              className="w-full"
              filter
              emptyFilterMessage={t("no_results_found")}
            />
            {formik.touched.course &&
              formik.errors.course !== "Required" &&
              formik.errors.course !== "مطلوب" && (
                <p className="mt-1 text-start text-xs text-red-300">{formik.errors.course}</p>
              )}
          </div>
          {isSchool && (
            <div>
              <div className="mb-1 flex items-end space-x-1">
                <label className="block text-sm font-normal text-gray-800 dark:text-white">
                  {t("teacher")}
                </label>
                {formik.touched.teacher &&
                  (formik.errors.teacher === "Required" || formik.errors.teacher === "مطلوب") && (
                    <span className="text-start text-xl leading-3 text-red-300">*</span>
                  )}
              </div>
              <Dropdown
                pt={{
                  input:
                    "p-0 text-sm placeholder:text-sm placeholder:text-mutedText dark:text-dark-mutedText dark:text-white",
                  root: "w-full rounded-md border bg-[#F9FAFB] px-3 py-2 focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-10 dark:border-dark-background dark:bg-dark-background",
                }}
                value={formik.values.teacher}
                onChange={(e) => formik.setFieldValue("teacher", e.value)}
                options={teachers}
                optionLabel="label"
                optionValue="value"
                placeholder={t("assign_to")}
                className="w-full"
                loading={loadingTeachers}
                filter
                emptyFilterMessage={t("no_results_found")}
              />
              {formik.touched.teacher &&
                formik.errors.teacher !== "Required" &&
                formik.errors.teacher !== "مطلوب" && (
                  <p className="mt-1 text-start text-xs text-red-300">{formik.errors.teacher}</p>
                )}
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
});

export default AddClassroomModal;
